import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Confirm, FlexHorizontal, IBoard, LoadMask, MessageCard, Spacer, useToasts } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import InfiniteScroll from 'react-infinite-scroller'
import { MessageModal } from '../../message-v2'
import { MessageTemplateTabs } from '../components/Tabs'
import { TruncatedId } from '../../truncated-id'
import { tryAsync } from '../../../utils/tryAsync'
import { useDeleteTemplateMessageSuggestion } from '../hooks/useDeleteTemplateMessageSuggestion'
import { useHistory } from 'react-router'
import { useListTemplateMessageSuggestions } from '../hooks/useListTemplateMessageSuggestions'

const PER_PAGE = 25

export const ListSuggestions = () => {
  const [messageId, setMessageId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const { loading, error, data, refetch, fetchMore } = useListTemplateMessageSuggestions({
    input: {
      limit: PER_PAGE,
      cursor: null
    }
  })
  const [deleteSuggestion, { loading: deletingSuggestionLoading }] = useDeleteTemplateMessageSuggestion()
  const { addToast } = useToasts()
  const history = useHistory()

  return (
    <Box>
      {deletingSuggestionLoading ? <LoadMask /> : null}
      <Confirm
        title='Delete Template Suggestion? This cannot be undone!'
        handleClose={() => {
          setIsDeleting(null)
        }}
        open={!!isDeleting}
        handleAccept={async () => {
          const { result } = await tryAsync(() =>
            deleteSuggestion({
              variables: {
                id: isDeleting || ''
              }
            })
          )

          if (result && result.data?.deleteTemplateMessageSuggestion.__typename === 'TemplateMessageSuggestionV2') {
            addToast('Template suggestion deleted successfully', {
              appearance: 'success'
            })
            await refetch()
          } else {
            addToast('There was an error deleting your template message', {
              appearance: 'error',
              autoDismiss: true
            })
          }
          setIsDeleting(null)
        }}
      />
      <MessageModal messageId={messageId} onClose={() => setMessageId(null)} />
      <FlexHorizontal>
        <Typography variant='h4'>Template Suggestions</Typography>
      </FlexHorizontal>
      <Box>
        <Spacer size='extraLarge' />
        <MessageTemplateTabs activeTab='suggestions' />
      </Box>
      <Spacer size='large' />
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the template suggestions</Alert>
      ) : !data?.listTemplateMessageSuggestions.templateMessageSuggestions.length ? (
        <Alert severity='info'>There are no template suggestions</Alert>
      ) : (
        <InfiniteScroll
          loader={
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <Spacer size='large' />
              <CircularProgress />
            </Box>
          }
          hasMore={!!data?.listTemplateMessageSuggestions.nextCursor && !loading}
          loadMore={async () => {
            const nextCursor = data?.listTemplateMessageSuggestions.nextCursor

            if (nextCursor) {
              await fetchMore({
                variables: {
                  input: {
                    cursor: nextCursor,
                    limit: PER_PAGE
                  }
                },
                updateQuery: (previousQueryResult, options) => {
                  const fetchMoreResult = options.fetchMoreResult
                  const prev = previousQueryResult

                  return {
                    listTemplateMessageSuggestions: {
                      __typename: 'ListTemplateMessageSuggestionsV2',
                      templateMessageSuggestions: [
                        ...prev.listTemplateMessageSuggestions.templateMessageSuggestions,
                        ...(fetchMoreResult?.listTemplateMessageSuggestions?.templateMessageSuggestions || [])
                      ],
                      nextCursor: fetchMoreResult?.listTemplateMessageSuggestions?.nextCursor || null
                    }
                  }
                }
              })
            }
          }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 360 }}>ID</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell style={{ width: 250 }}>Message</TableCell>
                  <TableCell style={{ width: 200 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.listTemplateMessageSuggestions.templateMessageSuggestions.map(colorTemplate => (
                  <TableRow key={colorTemplate.id}>
                    <TableCell>
                      <TruncatedId value={colorTemplate.id} full path={`/message/${colorTemplate.message.id}`} />
                    </TableCell>
                    <TableCell>{colorTemplate.message.text}</TableCell>
                    <TableCell>
                      <Box>
                        <MessageCard
                          characters={colorTemplate.message.characters as IBoard}
                          boardStyle={colorTemplate?.message?.cardSize === '15x3' ? 'note-black' : 'black'}
                          onEditPress={() => {
                            setMessageId(colorTemplate.message.id)
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FlexHorizontal>
                        <Button
                          style={{
                            marginRight: 8
                          }}
                          size='large'
                          color='secondary'
                          variant='contained'
                          onClick={() => {
                            setIsDeleting(colorTemplate.id)
                          }}>
                          Delete
                        </Button>
                        <Button
                          size='large'
                          color='primary'
                          variant='contained'
                          onClick={() => {
                            history.push(`/message-templates/message/create?messageId=${colorTemplate.message.id}`)
                          }}>
                          Accept
                        </Button>
                      </FlexHorizontal>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      )}
    </Box>
  )
}
